import { useId, useMemo } from 'react'
import { cn } from '@app/lib/cn'
import { rotate, translate } from '../../../worker/lib/yield'
import type { PieceScale } from './live-calc'

/**
 * The ölçek alanı, drawn as what it MEANS: a panel of material with the piece
 * stamped across it on its own (u, v) lattice — the cutter laying this one
 * part again and again, k = 2 mates interlocked at their reported offset. One
 * parallelogram cell is picked out in dashed çelik with its area tinted (that
 * cell is the "ölçek desi/adet" figure), the emphasized copy of the piece is
 * kordovan, and every other stamp is ink-muted so the field reads as
 * repetition rather than as many selections.
 *
 * The panel is sized in PER-PIECE units (√(cell area / k)), so a small piece
 * and a large one fill it with the same density, and so do k = 1 and k = 2:
 * the picture is about the METHOD, not the absolute millimetres. Stamps are
 * clipped at the panel edge exactly the way a cutter runs off the hide.
 *
 * Purely presentational; millimetre viewBox, y flipped here (the lattice is
 * y-up mm geometry), CSS-variable colors like `StripSvg`.
 */

export interface LatticeSvgProps {
  /** Decimated outer ring of the piece, `[x0, y0, …]` mm — NOT yet rotated. */
  outlineMm: Float32Array
  scale: PieceScale
  className?: string
}

/** Panel size in per-piece units — wide, like the strips below it. */
const PANEL_COLS = 7
const PANEL_ROWS = 3

/** Hard caps so a degenerate lattice cannot stall the tab. */
const MAX_STEPS = 40
const MAX_TILES = 400

export function LatticeSvg({ outlineMm, scale, className }: LatticeSvgProps) {
  // SVG ids are document-global; two lattices must not share one clip.
  const clipId = `lattice-clip-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`

  const scene = useMemo(() => {
    const base = rotate(unpack(outlineMm), scale.angleDeg)
    const mate =
      scale.k === 2 && scale.mateOffsetMm
        ? translate(
            rotate(unpack(outlineMm), scale.angleDeg + 180),
            scale.mateOffsetMm[0],
            scale.mateOffsetMm[1],
          )
        : null

    // The motif — one piece, or the interlocked pair — is what the lattice
    // repeats; every stamp on the panel is the motif at i·u + j·v.
    const motif: [number, number][][] = mate ? [base, mate] : [base]
    const motifBox = bounds(motif.flat())

    const u = scale.u
    const v = scale.v
    const det = u[0] * v[1] - u[1] * v[0]
    // The panel's length unit: the side of a square with the area ONE PIECE
    // takes, which is the cell divided by the k pieces the cell holds. Sizing
    // on the cell itself would make a k = 2 field twice as dense as a k = 1 one
    // — the same number of cells, but two stamps in each — and the density is
    // meant to be the constant across every piece the tab draws.
    const unit = Math.sqrt(Math.max(Math.abs(det) / Math.max(scale.k, 1), 1))

    // Panel centered on the motif, so the emphasized copy sits mid-field.
    const cx = (motifBox.minX + motifBox.maxX) / 2
    const cy = (motifBox.minY + motifBox.maxY) / 2
    const panel = {
      minX: cx - (PANEL_COLS * unit) / 2,
      maxX: cx + (PANEL_COLS * unit) / 2,
      minY: cy - (PANEL_ROWS * unit) / 2,
      maxY: cy + (PANEL_ROWS * unit) / 2,
    }

    /*
     * Which lattice offsets t = i·u + j·v put a stamp ON the panel: exactly
     * those with t inside the panel dilated by the motif's own box. The (i, j)
     * ranges come from the inverse basis at that region's corners, then every
     * candidate is checked exactly — the ranges only bound the walk.
     */
    const region = {
      minX: panel.minX - motifBox.maxX,
      maxX: panel.maxX - motifBox.minX,
      minY: panel.minY - motifBox.maxY,
      maxY: panel.maxY - motifBox.minY,
    }
    let minI = 0
    let maxI = 0
    let minJ = 0
    let maxJ = 0
    if (det !== 0) {
      const corners: [number, number][] = [
        [region.minX, region.minY],
        [region.minX, region.maxY],
        [region.maxX, region.minY],
        [region.maxX, region.maxY],
      ]
      minI = Infinity
      maxI = -Infinity
      minJ = Infinity
      maxJ = -Infinity
      for (const [x, y] of corners) {
        const i = (x * v[1] - y * v[0]) / det
        const j = (u[0] * y - u[1] * x) / det
        if (i < minI) minI = i
        if (i > maxI) maxI = i
        if (j < minJ) minJ = j
        if (j > maxJ) maxJ = j
      }
    }
    const i0 = Math.max(Math.floor(minI), -MAX_STEPS)
    const i1 = Math.min(Math.ceil(maxI), MAX_STEPS)
    const j0 = Math.max(Math.floor(minJ), -MAX_STEPS)
    const j1 = Math.min(Math.ceil(maxJ), MAX_STEPS)

    const offsets: [number, number][] = []
    outer: for (let i = i0; i <= i1; i += 1) {
      for (let j = j0; j <= j1; j += 1) {
        if (i === 0 && j === 0) continue // the emphasized copy, drawn apart
        const tx = i * u[0] + j * v[0]
        const ty = i * u[1] + j * v[1]
        if (tx < region.minX || tx > region.maxX || ty < region.minY || ty > region.maxY) {
          continue
        }
        offsets.push([tx, ty])
        if (offsets.length >= MAX_TILES) break outer
      }
    }

    // The cell hangs off the motif's own lower-left corner: any anchor tiles
    // the same lattice, and this one keeps the cell visually on the piece.
    const anchor: [number, number] = [motifBox.minX, motifBox.minY]
    const cell: [number, number][] = [
      anchor,
      [anchor[0] + u[0], anchor[1] + u[1]],
      [anchor[0] + u[0] + v[0], anchor[1] + u[1] + v[1]],
      [anchor[0] + v[0], anchor[1] + v[1]],
    ]

    // Millimetres are y-up; SVG is y-down. Flip inside the panel, which maps
    // the panel rect onto itself — the viewBox IS the panel.
    const flip = ([x, y]: readonly [number, number]): [number, number] => [
      x,
      panel.minY + panel.maxY - y,
    ]

    return {
      panel,
      piece: pathOf(base.map(flip)),
      mate: mate ? pathOf(mate.map(flip)) : null,
      tiles: offsets.flatMap((offset) =>
        motif.map((ring) => pathOf(translate(ring, offset[0], offset[1]).map(flip))),
      ),
      cell: pathOf(cell.map(flip)),
    }
  }, [outlineMm, scale])

  const { panel } = scene
  const panelWidth = panel.maxX - panel.minX
  const panelHeight = panel.maxY - panel.minY

  return (
    <svg
      viewBox={`${round(panel.minX)} ${round(panel.minY)} ${round(panelWidth)} ${round(panelHeight)}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Ölçek alanı yerleşimi"
      className={cn('block h-full w-full', className)}
    >
      {/* The panel: bare paper framed by a hairline, like the strips below. */}
      <rect
        x={round(panel.minX)}
        y={round(panel.minY)}
        width={round(panelWidth)}
        height={round(panelHeight)}
        fill="var(--color-kagit)"
        stroke="var(--color-hairline)"
        vectorEffect="non-scaling-stroke"
      />
      <clipPath id={clipId}>
        <rect
          x={round(panel.minX)}
          y={round(panel.minY)}
          width={round(panelWidth)}
          height={round(panelHeight)}
        />
      </clipPath>
      <g clipPath={`url(#${clipId})`}>
        {scene.tiles.map((d, at) => (
          <path
            key={at}
            data-role="tile"
            d={d}
            fill="color-mix(in srgb, var(--color-ink) 7%, transparent)"
            stroke="color-mix(in srgb, var(--color-ink-muted) 60%, transparent)"
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
          />
        ))}
        <path
          data-role="cell"
          d={scene.cell}
          fill="color-mix(in srgb, var(--color-celik) 10%, transparent)"
          stroke="var(--color-celik)"
          strokeWidth={1}
          strokeDasharray="4 3"
          vectorEffect="non-scaling-stroke"
        />
        <path
          data-role="piece"
          d={scene.piece}
          fill="color-mix(in srgb, var(--color-kordovan) 12%, transparent)"
          stroke="var(--color-kordovan)"
          strokeWidth={1.5}
          vectorEffect="non-scaling-stroke"
        />
        {scene.mate ? (
          <path
            data-role="mate"
            d={scene.mate}
            fill="color-mix(in srgb, var(--color-kordovan) 12%, transparent)"
            stroke="var(--color-kordovan)"
            strokeWidth={1.5}
            vectorEffect="non-scaling-stroke"
          />
        ) : null}
      </g>
    </svg>
  )
}

function unpack(outline: Float32Array): [number, number][] {
  const points: [number, number][] = new Array(outline.length / 2)
  for (let i = 0; i < points.length; i += 1) {
    points[i] = [outline[2 * i] as number, outline[2 * i + 1] as number]
  }
  return points
}

function bounds(points: readonly (readonly [number, number])[]): {
  minX: number
  minY: number
  maxX: number
  maxY: number
} {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const [x, y] of points) {
    if (x < minX) minX = x
    if (x > maxX) maxX = x
    if (y < minY) minY = y
    if (y > maxY) maxY = y
  }
  return { minX, minY, maxX, maxY }
}

function pathOf(ring: readonly (readonly [number, number])[]): string {
  if (ring.length === 0) return ''
  let d = `M${round(ring[0]![0])} ${round(ring[0]![1])}`
  for (let i = 1; i < ring.length; i += 1) {
    d += `L${round(ring[i]![0])} ${round(ring[i]![1])}`
  }
  return `${d}Z`
}

function round(value: number): number {
  return Math.round(value * 100) / 100
}
